import { useState, useEffect } from "react";
import { UserProjectsApi } from "../../api/UserProjectsApi";
// import { saveProjectAuth, getProjectAuth, clearProjectAuth } from "../../utils/projectAuthSession";
import { getSession } from "../../utils/authSession";
import { Loader } from "react-feather";
import "./UserProjects.css";

/* SAME email validation logic */
function isValidEmail(email) {
    if (!email) return false;
    const normalized = email.toLowerCase();
    if (!normalized.includes("@") || !normalized.includes(".")) return false;
    if (normalized.includes("test")) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

function formatDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(); // date + time
}

function stripHtmlToText(html) {
    if (!html) return "";
    return String(html)
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/<\/(p|div|br|li|h1|h2|h3|h4|h5|h6)>/gi, "\n")
        .replace(/<li[^>]*>/gi, "• ")
        .replace(/<[^>]+>/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

export default function UserProjects({ onOpenRevision }) {

    const session = getSession();
    const [email] = useState(session?.email || "");

    const [projects, setProjects] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const emailValid = isValidEmail(email);

    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    const [loadingProjectId, setLoadingProjectId] = useState(null);

    useEffect(() => {
        if (emailValid) {
            loadProjects(email, false);
        }
    }, [email]);

    async function loadProjects(userEmail, loadMore = false) {
        setLoading(true);
        setError(null);

        try {
            const res = await UserProjectsApi.getCompanyUserProjects(
                userEmail,
                loadMore ? cursor : null
            );

            setProjects((prev) =>
                loadMore ? [...prev, ...res.projects] : res.projects
            );

            setCursor(res.nextCursor || null);
            setHasMore(Boolean(res.hasMore));

            localStorage.setItem("lastUsedEmail", userEmail);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    function toggleExpand(projectId) {
        setExpanded((prev) => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    }

    return (
        <main className="page user-projects">
            <div className="login-header">
                <div className="login-icon">📁</div>

                <div>
                    <h2>My Projects</h2>
                    <p>Access all your AI 4 Signs proposals.</p>
                </div>
            </div>

            {error && <p className="error">{error}</p>}
            {loading && (
                <div className="projects-loading">
                    <Loader size={20} className="spin" />
                    <span>Loading projects...</span>
                </div>
            )}

            <div className="projects-grid">
                {projects.map((project) => {
                    const isOpen = expanded[project.projectId];

                    const createdAt = formatDate(project.createdAt);
                    const completedAt = formatDate(project.completedAt);
                    const aiGeneratedAt = formatDate(project.aiText?.generatedAt);

                    const contactEmail =
                        project.contact?.email || project.email || email || "";

                    // If you prefer plain text preview (optional), use this:
                    const plainDesc = stripHtmlToText(project.aiText?.html || "");

                    const proposalUrl = project.pdf?.url || project.pdfUrl || "";
                    const proposalName = project.pdf?.fileName || "proposal.pdf";

                    const sowUrl = project.prePdf?.url || project.prePdfUrl || "";
                    const sowName = project.prePdf?.fileName || "setup.pdf";

                    const status = project.status || "unknown";

                    return (
                        <div key={project.projectId} className="project-card">
                            {project.previewImage && (
                                <div className="project-image">
                                    <img
                                        src={project.previewImage}
                                        alt={project.projectName || "Project"}
                                        loading="lazy"
                                    />
                                </div>
                            )}

                            <div className="project-body">
                                <h3>{project.projectName || "Untitled project"}</h3>

                                {/* ✅ Existing meta row (kept) + added more dates */}
                                <div className="project-meta">
                                    <span className={`status ${status}`}>{status}</span>

                                    {/* Keep completedAt visible as you requested */}
                                    {completedAt && <span>{completedAt}</span>}
                                </div>

                                {/* ✅ NEW: More details row (createdAt + contact email) */}
                                <div className="project-kv">
                                    {createdAt && (
                                        <div className="kv">
                                            <span className="k">Created</span>
                                            <span className="v">{createdAt}</span>
                                        </div>
                                    )}

                                    {contactEmail && (
                                        <div className="kv">
                                            <span className="k">Email</span>
                                            <span className="v mono">{contactEmail}</span>
                                        </div>
                                    )}

                                    {aiGeneratedAt && (
                                        <div className="kv">
                                            <span className="k">AI</span>
                                            <span className="v">
                                                {aiGeneratedAt}
                                                {project.aiText?.model ? ` • ${project.aiText.model}` : ""}
                                                {typeof project.aiText?.length === "number"
                                                    ? ` • ${project.aiText.length} chars`
                                                    : ""}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* ✅ Description (HTML) — kept (same behavior) */}
                                {project.aiText?.html && (
                                    <>
                                        <div
                                            className={`project-desc ${isOpen ? "open" : ""}`}
                                            dangerouslySetInnerHTML={{ __html: project.aiText.html }}
                                        />

                                        <button
                                            className="see-more"
                                            onClick={() => toggleExpand(project.projectId)}
                                        >
                                            {isOpen ? "See less" : "See more"}
                                        </button>
                                    </>
                                )}
                                {/* ✅ Existing actions (kept) + added filenames */}
                                <div className="project-actions">
                                    {proposalUrl && (
                                        <a
                                            href={proposalUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={proposalName}
                                        >
                                            Proposal
                                            <span className="file-hint">{proposalName}</span>
                                        </a>
                                    )}

                                    {sowUrl && (
                                        <a
                                            href={sowUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={sowName}
                                        >
                                            SOW
                                            <span className="file-hint">{sowName}</span>
                                        </a>
                                    )}

                                    {project.companyId && project.projectId && onOpenRevision && (
                                        <button
                                            className="ai-editor-btn"
                                            disabled={loadingProjectId === project.projectId}
                                            onClick={async () => {

                                                setLoadingProjectId(project.projectId);

                                                try {

                                                    await onOpenRevision(
                                                        project.companyId,
                                                        project.projectId
                                                    );

                                                } finally {

                                                    setLoadingProjectId(null);

                                                }

                                            }}
                                        >
                                            {loadingProjectId === project.projectId
                                                ? "Loading..."
                                                : "✨ AI Editor"}
                                        </button>
                                    )}

                                </div>

                                {/* ✅ Optional: show projectId smaller (helps debug / support) */}
                                {project.projectId && (
                                    <div className="project-id">ID: {project.projectId}</div>
                                )}

                                {/* ✅ Fallback: if html is missing but text exists, show plain text preview */}
                                {!project.aiText?.html && plainDesc && (
                                    <>
                                        <div className={`project-desc ${isOpen ? "open" : ""}`}>
                                            {plainDesc}
                                        </div>
                                        <button
                                            className="see-more"
                                            onClick={() => toggleExpand(project.projectId)}
                                        >
                                            {isOpen ? "See less" : "See more"}
                                        </button>
                                    </>
                                )}

                            </div>
                        </div>
                    );
                })}
            </div>

            {emailValid &&
                projects.length > 0 &&
                hasMore &&
                !loading && (
                    <button
                        className="load-more"
                        onClick={() => loadProjects(email, true)}
                    >
                        Load more
                    </button>
                )}

        </main>
    );
}
